import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  Visibility,
  Close,
  Receipt,
  Download,
  FilterList,
  RequestQuote,
  Payment,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import DynamicTable from "./Dynamic/DynamicTable";
import { useUser } from "../Context/UserContext";
import API_URLS from "../Services/api/action";
import { GetApi, PostApi, PutApi } from "../Services/api/ApiCall";
import { useRazorpayPayment } from "../utills/useRazorpayPayment";

const SpeciesRequest = () => {
  const { productionCenterId, token, production_type, authData } = useUser();

  const { pay: initiateRazorpayPayment } = useRazorpayPayment();

  const commonInputStyles = {
    "& .MuiInputLabel-root": { color: "black" },
    "& .MuiOutlinedInput-root": {
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "green" },
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "green" },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "green",
      },
    },
    "&.Mui-focused .MuiInputLabel-root": { color: "black" },
  };

  const handleNumberKeyDown = (e) => {
    if (["-", "e", "E", "+", "."].includes(e.key)) {
      e.preventDefault();
    }
  };

  /* ───────── STATE ───────── */
  const [orders, setOrders] = useState([]);
  const [stockData, setStockData] = useState([]);
  const [schemaList, setSchemaList] = useState([]);
  const [validTargetSchemes, setValidTargetSchemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editableItems, setEditableItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [billModalOpen, setBillModalOpen] = useState(false);
  const [billItems, setBillItems] = useState([]);
  const [billTotals, setBillTotals] = useState({
    subtotal: 0,
    discountTotal: 0,
    grandTotal: 0,
  });
  const [billPaymentType, setBillPaymentType] = useState("Cash");
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  /* ═══════════════════════════════════════════════
     FETCH FUNCTIONS
     ═══════════════════════════════════════════════ */

  const fetchTargetSchemes = async () => {
    if (!productionCenterId) {
      setValidTargetSchemes([]);
      return;
    }
    try {
      const res = await GetApi(
        `${API_URLS.GetTargetSchemes}?productioncenter_id=${productionCenterId}`,
        token,
      );
      setValidTargetSchemes(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error("Target schemes error:", err);
      setValidTargetSchemes([]);
    }
  };

  const fetchRequests = async () => {
    if (!productionCenterId) return;
    try {
      setLoading(true);
      const response = await axios.get(
        `/api${API_URLS.allocations}?production_center_id=${productionCenterId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setOrders(response.data.results || []);
    } catch (error) {
      console.error("Requests error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStock = async () => {
    if (!productionCenterId) return;
    try {
      const res = await GetApi(
        `${API_URLS.species}?production_center_id=${productionCenterId}`,
        token,
      );
      setStockData(res?.results || res?.data?.results || []);
    } catch (err) {
      console.error("Stock error:", err);
    }
  };

  const fetchSchemas = async () => {
    try {
      const res = await GetApi(`${API_URLS.scheme}`, token);
      setSchemaList(res?.results || res?.data?.results || []);
    } catch (err) {
      console.error("Schema error:", err);
    }
  };

  useEffect(() => {
    if (productionCenterId) {
      fetchRequests();
      fetchStock();
      fetchSchemas();
      fetchTargetSchemes();
    }
  }, [productionCenterId, production_type]);

  useEffect(() => {
    return () => {
      if (pdfUrl)
        URL.revokeObjectURL(pdfUrl.replace(/#toolbar=0&navpanes=0/g, ""));
    };
  }, [pdfUrl]);

  /* ═══════════════════════════════════════════════
     FILTER
     ═══════════════════════════════════════════════ */
  const filteredOrders = orders.filter((o) =>
    !statusFilter ? true : o.order_status === statusFilter,
  );

  /* ═══════════════════════════════════════════════
     HELPERS
     ═══════════════════════════════════════════════ */
  const getAvailableStock = (stock_id) =>
    stockData.find((s) => s.id === stock_id)?.saplings_available || 0;

  const getBaseRate = (stock_id) =>
    parseFloat(stockData.find((s) => s.id === stock_id)?.price_per_sapling) ||
    0;

  const getSchemesForSpecies = (speciesId) => {
    const bySpecies = schemaList.filter(
      (s) =>
        s.species_preferred &&
        s.species_preferred.some((sp) => sp.id === speciesId),
    );
    const targetIds = new Set(validTargetSchemes.map((v) => v.id));
    return bySpecies.filter((s) => targetIds.has(s.id));
  };

  const getTargetInfo = (schemeId) =>
    validTargetSchemes.find((v) => v.id === schemeId);

  const calculateItemFinancials = (item, quantityField = "auto") => {
    const baseRate = item.base_rate || getBaseRate(item.stock_id);
    let finalRate = baseRate;

    if (item.type === "scheme" && item.scheme_id) {
      const scheme = schemaList.find((s) => s.id === item.scheme_id);
      if (scheme?.percentage) {
        finalRate = baseRate - (baseRate * parseFloat(scheme.percentage)) / 100;
      }
    }

    let qty = 0;
    if (quantityField === "quantity") {
      qty = parseInt(item.quantity) || 0;
    } else if (quantityField === "allocated_quantity") {
      qty = parseInt(item.allocated_quantity) || 0;
    } else {
      qty = parseInt(
        item.allocated_quantity || item.approved_quantity || item.quantity || 0,
      );
    }

    qty = Math.max(0, qty);

    return {
      base_rate: baseRate,
      final_rate: finalRate,
      base_total: baseRate * qty,
      line_total: finalRate * qty,
    };
  };

  /* ═══════════════════════════════════════════════
     ALLOCATION HANDLERS
     ═══════════════════════════════════════════════ */
  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setError("");

    const items = order.requested_items.map((item) => {
      const eligible = getSchemesForSpecies(item.species_id);
      const defaultType =
        eligible.length > 0 ? item.type || "scheme" : "non-scheme";

      const init = {
        ...item,
        available_stock: getAvailableStock(item.stock_id),
        allocated_quantity: item.approved_quantity || item.requested_quantity,
        current_status: item.item_status || "pending",
        type: defaultType,
        scheme_id:
          defaultType === "scheme" ? item.scheme_id || eligible[0]?.id : null,
      };
      return {
        ...init,
        ...calculateItemFinancials(init, "allocated_quantity"),
      };
    });

    setEditableItems(items);
    setModalOpen(true);
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...editableItems];
    const item = { ...updated[index] };

    if (field === "allocated_quantity") {
      let v = parseInt(value) || 0;
      if (v > item.available_stock) v = item.available_stock;
      if (v < 0) v = 0;
      item.allocated_quantity = v;
    } else if (field === "type") {
      item.type = value;
      if (value === "non-scheme") {
        item.scheme_id = null;
      } else if (value === "scheme") {
        const eligible = getSchemesForSpecies(item.species_id);
        if (eligible.length === 0) {
          item.type = "non-scheme";
          toast.warning(`No valid target schemes for ${item.species_name}.`);
        } else if (
          !item.scheme_id ||
          !eligible.find((s) => s.id === item.scheme_id)
        ) {
          item.scheme_id = eligible[0].id;
        }
      }
    } else if (field === "scheme_id") {
      item.scheme_id = value ? value.id : null;
      item.type = value ? "scheme" : "non-scheme";
    } else if (field === "status") {
      item.current_status = value;
    }

    Object.assign(item, calculateItemFinancials(item, "allocated_quantity"));
    updated[index] = item;
    setEditableItems(updated);
  };

  const handleSubmitAllocation = async () => {
    const pending = editableItems.find((i) => i.current_status === "pending");
    if (pending) {
      setError(`Please Approve or Reject "${pending.species_name}" first.`);
      return;
    }

    const invalid = editableItems.find(
      (i) =>
        i.allocated_quantity > i.available_stock &&
        i.current_status === "approved",
    );
    if (invalid) {
      setError(`Invalid quantity for ${invalid.species_name}.`);
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      await Promise.all(
        editableItems.map((item) =>
          axios.put(
            `/api${API_URLS.PutApproveitem}${item.item_id}`,
            {
              action: item.current_status === "rejected" ? "reject" : "approve",
              approved_quantity: item.allocated_quantity,
              type: item.type,
              scheme_id: item.scheme_id,
              schemed_rate: item.final_rate,
              total_amount: item.base_total,
            },
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        ),
      );

      await axios.put(
        `/api${API_URLS.Requestheader}${selectedOrder.request_id}`,
        { status: "order-placed" },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      toast.success("Allocation Saved!");
      setModalOpen(false);
      fetchRequests();
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || "Failed to process.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* ═══════════════════════════════════════════════
     BILLING HANDLERS
     ═══════════════════════════════════════════════ */
  const handleBillOpen = (order) => {
    setSelectedOrder(order);
    const items = order.requested_items
      .filter((i) => i.item_status === "approved" || i.approved_quantity > 0)
      .map((item) => {
        const init = {
          ...item,
          base_rate: getBaseRate(item.stock_id),
          type: item.type || "non-scheme",
          scheme_id: item.scheme_id || null,
          quantity: item.approved_quantity || 0,
        };
        return { ...init, ...calculateItemFinancials(init, "quantity") };
      });

    setBillItems(items);
    recalcTotals(items);
    setBillPaymentType("Cash");
    setBillModalOpen(true);
  };

  const recalcTotals = (items) => {
    const sub = items.reduce((sum, item) => {
      const val = Number(item.base_total) || 0;
      return sum + val;
    }, 0);

    const grand = items.reduce((sum, item) => {
      const val = Number(item.line_total) || 0;
      return sum + val;
    }, 0);

    const discount = sub - grand;

    setBillTotals({
      subtotal: Number(sub.toFixed(2)),
      discountTotal: Number(discount.toFixed(2)),
      grandTotal: Number(grand.toFixed(2)),
    });
  };

  const handleBillItemChange = (index, field, value) => {
    const arr = [...billItems];
    const item = { ...arr[index] };

    if (field === "quantity") {
      item.quantity = parseInt(value) || 0;
    } else if (field === "type") {
      item.type = value;
      if (value === "non-scheme") {
        item.scheme_id = null;
      } else {
        const el = getSchemesForSpecies(item.species_id);
        if (el.length > 0 && !item.scheme_id) item.scheme_id = el[0].id;
      }
    } else if (field === "scheme_id") {
      item.scheme_id = value ? value.id : null;
      item.type = value ? "scheme" : "non-scheme";
    }

    Object.assign(item, calculateItemFinancials(item, "quantity"));
    arr[index] = item;
    setBillItems(arr);
    recalcTotals(arr);
  };

  const handleBillSubmit = async () => {
    if (!billPaymentType) {
      toast.error("Select payment type");
      return;
    }

    if (billPaymentType === "UPI") {
      if (billTotals.grandTotal <= 0) {
        toast.warning("Amount is 0. Select 'Free of Cost'.");
        return;
      }
      setPaymentProcessing(true);
      toast.info("Initiating payment...");
      try {
        await initiateRazorpayPayment({
          amount: billTotals.grandTotal,
          orderId: selectedOrder.orderid,
          customerName: selectedOrder.farmer_name || "Customer",
          customerEmail: selectedOrder.farmer_email || "customer@example.com",
          customerPhone: selectedOrder.farmer_phone || "9999999999",
          onSuccess: async (payRes) => {
            toast.success("Payment successful! Generating bill...");
            await submitBill(payRes);
            setPaymentProcessing(false);
          },
          onError: (e) => {
            toast.error(e || "Payment failed");
            setPaymentProcessing(false);
          },
        });
      } catch (e) {
        toast.error("Failed to initiate payment");
        setPaymentProcessing(false);
      }
    } else {
      await submitBill(null);
    }
  };

  const submitBill = async (payDet) => {
    setSubmitting(true);
    try {
      await PostApi(
        `${API_URLS.Postorderplc}`,
        {
          request_id: selectedOrder.request_id,
          payment_type: billPaymentType,
          total_amount: billTotals.subtotal,
          scheme_total_amount: billTotals.grandTotal,
          ...(payDet && {
            razorpay_payment_id: payDet.razorpay_payment_id,
            razorpay_order_id: payDet.razorpay_order_id,
            razorpay_signature: payDet.razorpay_signature,
            payment_verified: true,
          }),
          items: billItems.map((i) => ({
            id: i.item_id,
            quantity: i.quantity,
            type: i.type,
            scheme_id: i.scheme_id,
            schemed_rate: i.final_rate,
            base_rate: i.base_rate,
            total_amount: i.base_total,
          })),
        },
        token,
      );
      toast.success("Bill Generated!");
      setBillModalOpen(false);
      fetchRequests();
    } catch (err) {
      toast.error(
        "Billing failed: " + (err.response?.data?.error || err.message),
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* ═══════════════════════════════════════════════
     PDF HANDLERS
     ═══════════════════════════════════════════════ */
  const pdfUrl_ = (id) => `api${API_URLS.GenerateBill}?order_id=${id}`;

  const handlePreviewBill = async (id) => {
    setPdfLoading(true);
    setPdfUrl("");
    setPreviewOpen(true);
    try {
      const r = await axios.get(pdfUrl_(id), { responseType: "blob" });
      setPdfUrl(
        `${URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }))}#toolbar=0&navpanes=0`,
      );
    } catch {
      toast.error("PDF load failed.");
      setPreviewOpen(false);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadBill = async (id) => {
    try {
      const r = await axios.get(pdfUrl_(id), { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(
        new Blob([r.data], { type: "application/pdf" }),
      );
      a.download = `Bill_${id}.pdf`;
      a.click();
    } catch {
      toast.error("Download failed.");
    }
  };

  /* ═══════════════════════════════════════════════
     TABLE COLUMNS
     ═══════════════════════════════════════════════ */
  const hasPending = editableItems.some((i) => i.current_status === "pending");
  const isProcessing = submitting || paymentProcessing;

  const columns = [
    {
      field: "orderid",
      headerName: "Order ID",
      sortable: true,
      render: (r) => (
        <Typography fontWeight={700} variant="body2">
          {r.orderid}
        </Typography>
      ),
    },
    {
      field: "farmer_name",
      headerName: "Name",
      hideOnMobile: true,
      render: (r) => (
        <Typography fontWeight={700} variant="body2">
          {r?.farmer_details?.farmer_name || "ldm"}
        </Typography>
      ),
    },
    {
      field: "farmer_type",
      headerName: "Type",
      render: (r) => {
        const f = r.farmer_type === "farmer";
        console.log("lof details", f);

        return (
          <Chip
            label={f ? "Farmer" : "Non-Farmer"}
            variant="outlined"
            size="small"
            sx={{
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              borderColor: f ? "#2e7d32" : "#836464",
              color: f ? "#2e7d32" : "#836464",
              backgroundColor: f ? "#e8f5e9" : "#f5f5f5",
            }}
          />
        );
      },
    },
    {
      field: "order_status",
      headerName: "Status",
      render: (r) => {
        const c = {
          pending: "warning",
          "order-placed": "info",
          billed: "success",
          rejected: "error",
        };
        return (
          <Chip
            label={r.order_status?.replace(/-/g, " ").toUpperCase()}
            color={c[r.order_status] || "default"}
            size="small"
            sx={{ fontWeight: 600, textTransform: "uppercase" }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      render: (r) => (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {r.order_status === "order-placed" && (
            <Button
              size="small"
              variant="contained"
              color="secondary"
              startIcon={<Receipt />}
              onClick={() => handleBillOpen(r)}
              sx={{ boxShadow: 2 }}
            >
              Bill
            </Button>
          )}
          {r.order_status === "pending" && (
            <Button
              size="small"
              variant="contained"
              startIcon={<Visibility />}
              onClick={() => handleViewOrder(r)}
              sx={{
                bgcolor: "#ffc022",
                color: "black",
                "&:hover": { bgcolor: "#e6ac00" },
                boxShadow: 2,
              }}
            >
              Allocate
            </Button>
          )}
          {r.order_status === "billed" && (
            <>
              <Tooltip title="Preview PDF">
                <IconButton
                  color="primary"
                  onClick={() => handlePreviewBill(r.orderid)}
                  sx={{ border: "1px solid", borderColor: "primary.main" }}
                >
                  <Visibility fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download PDF">
                <IconButton
                  color="success"
                  onClick={() => handleDownloadBill(r.orderid)}
                  sx={{ border: "1px solid", borderColor: "success.main" }}
                >
                  <Download fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ];

  /* ═══════════════════════════════════════════════
     SHARED UI BITS
     ═══════════════════════════════════════════════ */
  const cellSx = { py: 1, px: 0.5 };
  const paperSx = { borderRadius: 3, boxShadow: "0 10px 30px rgba(0,0,0,.15)" };

  const renderSchemeOption = (props, option) => {
    const t = getTargetInfo(option.id);
    return (
      <li {...props} style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {option.name}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5, mt: 0.3 }}>
            <Chip
              label={`${option.percentage}%`}
              size="small"
              sx={{
                height: 18,
                fontSize: ".65rem",
                bgcolor: "#fff3e0",
                color: "#e65100",
              }}
            />
            {t && (
              <Chip
                label={`T:${t.target_quantity}`}
                size="small"
                sx={{
                  height: 18,
                  fontSize: ".65rem",
                  bgcolor: "#e8f5e9",
                  color: "#2e7d32",
                }}
              />
            )}
          </Box>
        </Box>
      </li>
    );
  };

  /* ═══════════════════════════════════════════════
     ✅ BILL TABLE COLUMN DEFINITIONS (Compact)
     ═══════════════════════════════════════════════ */
  const billColumns = [
    { id: "species", label: "Species", width: "16%", align: "left" },
    { id: "qty", label: "Qty", width: "7%", align: "center" },
    { id: "baseRate", label: "Base ₹", width: "9%", align: "right" },
    { id: "type", label: "Type", width: "10%", align: "center" },
    { id: "scheme", label: "Scheme", width: "20%", align: "center" },
    { id: "percent", label: "%", width: "6%", align: "center" },
    { id: "finalRate", label: "Rate ₹", width: "9%", align: "right" },
    { id: "total", label: "Total ₹", width: "10%", align: "right" },
  ];

  /* ═══════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════ */
  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={2500}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        draggable
        pauseOnHover
        theme="colored"
      />
      <style>{`*{scrollbar-width:none;-ms-overflow-style:none}*::-webkit-scrollbar{display:none}`}</style>

      <Box
        sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: 1400, margin: "0 auto" }}
      >
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            height: "100%",
            borderColor: "#e0e0e0",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Typography variant="h5" fontWeight={800} sx={{ color: "#2e7d32" }}>
              Ordebrs
            </Typography>
            <FormControl
              size="small"
              sx={{ minWidth: { xs: "100%", sm: 200 } }}
            >
              <InputLabel>
                <FilterList sx={{ mr: 1, fontSize: 16 }} />
                Status
              </InputLabel>
              <Select
                value={statusFilter || ""}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="order-placed">Order Placed</MenuItem>
                <MenuItem value="billed">Billed</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <DynamicTable
            data={filteredOrders}
            columns={columns}
            loading={loading}
          />
        </Paper>

        {/* ═════════════════ ALLOCATION MODAL ═════════════════ */}
        <Dialog
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: paperSx }}
        >
          <DialogTitle
            sx={{
              bgcolor: "#f5f5f5",
              py: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight={700} color="primary">
              Process Order: {selectedOrder?.orderid}
            </Typography>
            <IconButton onClick={() => setModalOpen(false)}>
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ p: { xs: 1.5, md: 3 } }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {validTargetSchemes.length > 0 && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                <strong>Available Target Schemes:</strong>{" "}
                {validTargetSchemes.map((s) => s.name).join(", ")}
              </Alert>
            )}
            {validTargetSchemes.length === 0 && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                No target schemes with quantity found for this center. Only
                non-scheme options available.
              </Alert>
            )}

            <TableContainer
              component={Paper}
              elevation={0}
              variant="outlined"
              sx={{ overflowX: "auto", borderRadius: 2 }}
            >
              <Table size="small">
                <TableHead sx={{ bgcolor: "#2e7d32" }}>
                  <TableRow>
                    {[
                      "Species",
                      "Req",
                      "Stock",
                      "Qty",
                      "Type",
                      "Scheme",
                      "Details",
                      "Action",
                    ].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          color: "#fff",
                          fontWeight: 700,
                          py: 1.5,
                          textAlign: "center",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {editableItems.map((item, idx) => {
                    const eligible = getSchemesForSpecies(item.species_id);
                    const selected = eligible.find(
                      (s) => s.id === item.scheme_id,
                    );
                    const tInfo = selected ? getTargetInfo(selected.id) : null;

                    return (
                      <TableRow
                        key={item.item_id}
                        hover
                        sx={{ "&:nth-of-type(odd)": { bgcolor: "#fafafa" } }}
                      >
                        <TableCell sx={cellSx}>{item.species_name}</TableCell>
                        <TableCell align="center" sx={cellSx}>
                          {item.requested_quantity}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            ...cellSx,
                            color:
                              item.available_stock < item.requested_quantity
                                ? "error.main"
                                : "success.main",
                            fontWeight: 700,
                          }}
                        >
                          {item.available_stock}
                        </TableCell>

                        <TableCell align="center" sx={cellSx}>
                          <TextField
                            type="number"
                            size="small"
                            value={item.allocated_quantity}
                            onChange={(e) =>
                              handleItemChange(
                                idx,
                                "allocated_quantity",
                                e.target.value,
                              )
                            }
                            onKeyDown={handleNumberKeyDown}
                            inputProps={{
                              style: { width: 60, textAlign: "center" },
                              min: 0,
                            }}
                            sx={commonInputStyles}
                          />
                        </TableCell>

                        <TableCell align="center" sx={cellSx}>
                          <Select
                            size="small"
                            value={item.type}
                            onChange={(e) =>
                              handleItemChange(idx, "type", e.target.value)
                            }
                            sx={{ minWidth: 100 }}
                          >
                            <MenuItem value="non-scheme">Non-Scheme</MenuItem>
                            {eligible.length > 0 && (
                              <MenuItem value="scheme">Scheme</MenuItem>
                            )}
                          </Select>
                        </TableCell>

                        <TableCell
                          align="center"
                          sx={{ ...cellSx, minWidth: 200 }}
                        >
                          <Autocomplete
                            size="small"
                            options={eligible}
                            getOptionLabel={(o) => o.name}
                            value={selected || null}
                            onChange={(e, v) =>
                              handleItemChange(idx, "scheme_id", v)
                            }
                            disabled={item.type !== "scheme"}
                            renderOption={renderSchemeOption}
                            sx={commonInputStyles}
                            renderInput={(p) => (
                              <TextField {...p} placeholder="Select Scheme" />
                            )}
                          />
                        </TableCell>

                        <TableCell
                          align="center"
                          sx={{
                            ...cellSx,
                            whiteSpace: "nowrap",
                            minWidth: 120,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 0.3,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color:
                                  item.type === "scheme" ? "#d84315" : "grey",
                                fontWeight: 700,
                                lineHeight: 1,
                              }}
                            >
                              {item.type === "scheme" && selected
                                ? `${selected.percentage}%`
                                : "-"}
                            </Typography>
                            <Typography
                              fontWeight="bold"
                              color={
                                item.type === "scheme"
                                  ? "secondary.main"
                                  : "text.primary"
                              }
                              sx={{ lineHeight: 1.2 }}
                            >
                              ₹{item.final_rate.toFixed(2)}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell align="center" sx={cellSx}>
                          {item.current_status === "approved" ? (
                            <Chip
                              label="Approved"
                              color="success"
                              size="small"
                              onDelete={() =>
                                handleItemChange(idx, "status", "pending")
                              }
                              deleteIcon={<Close />}
                            />
                          ) : item.current_status === "rejected" ? (
                            <Chip
                              label="Rejected"
                              color="error"
                              size="small"
                              onDelete={() =>
                                handleItemChange(idx, "status", "pending")
                              }
                              deleteIcon={<Close />}
                            />
                          ) : (
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <Button
                                size="small"
                                color="success"
                                variant="outlined"
                                onClick={() =>
                                  handleItemChange(idx, "status", "approved")
                                }
                              >
                                App
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                onClick={() =>
                                  handleItemChange(idx, "status", "rejected")
                                }
                              >
                                Rej
                              </Button>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>

          <DialogActions sx={{ p: 2, bgcolor: "#fafafa" }}>
            <Button
              onClick={() => setModalOpen(false)}
              color="inherit"
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitAllocation}
              disabled={submitting || hasPending}
              sx={{ borderRadius: 2, px: 4, boxShadow: 2 }}
            >
              {submitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Confirm Allocation"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ═══════════════════════════════════════════════════════════════
             ✅ BILLING MODAL - COMPACT TABLE (No Horizontal Scroll)
             ═══════════════════════════════════════════════════════════════ */}
        <Dialog
          open={billModalOpen}
          onClose={() => !isProcessing && setBillModalOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: paperSx }}
        >
          <DialogTitle
            sx={{
              bgcolor: "#2e7d32",
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderRadius: "12px 12px 0 0",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontSize: "1rem",
              }}
            >
              <RequestQuote /> Generate Bill
            </Typography>
            <IconButton
              onClick={() => setBillModalOpen(false)}
              disabled={isProcessing}
              sx={{
                color: "#fff",
                "&:hover": { bgcolor: "rgba(255,255,255,.1)" },
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent
            dividers
            sx={{ p: { xs: 1, md: 2 }, overflow: "hidden" }}
          >
            {paymentProcessing && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  bgcolor: "rgba(255,255,255,.9)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 10,
                  borderRadius: 3,
                }}
              >
                <CircularProgress size={60} sx={{ color: "#2e7d32", mb: 2 }} />
                <Typography variant="h6" fontWeight={700} color="primary">
                  Processing Payment...
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Complete the payment in the popup
                </Typography>
                <Typography
                  variant="caption"
                  color="warning.main"
                  sx={{ mt: 2 }}
                >
                  Do not close this dialog
                </Typography>
              </Box>
            )}

            {/* ✅ COMPACT TABLE - Uses width: 100% and percentage-based columns */}
            <TableContainer
              component={Paper}
              elevation={0}
              variant="outlined"
              sx={{ mb: 2, borderRadius: 2, width: "100%", overflow: "hidden" }}
            >
              <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
                <TableHead sx={{ bgcolor: "#2e7d32" }}>
                  <TableRow>
                    {billColumns.map((col) => (
                      <TableCell
                        key={col.id}
                        sx={{
                          color: "#fff",
                          fontWeight: 700,
                          py: 1,
                          px: 0.5,
                          textAlign: col.align,
                          whiteSpace: "nowrap",
                          width: col.width,
                          fontSize: "0.75rem",
                          letterSpacing: "0.3px",
                        }}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {billItems.map((item, idx) => {
                    const eligible = getSchemesForSpecies(item.species_id);
                    const selected = eligible.find(
                      (s) => s.id === item.scheme_id,
                    );
                    return (
                      <TableRow
                        key={item.item_id}
                        sx={{ "&:nth-of-type(odd)": { bgcolor: "#fafafa" } }}
                      >
                        {/* Species - Truncated with tooltip */}
                        <TableCell
                          sx={{
                            ...cellSx,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Tooltip title={item.species_name} arrow>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.75rem", fontWeight: 600 }}
                              noWrap
                            >
                              {item.species_name}
                            </Typography>
                          </Tooltip>
                        </TableCell>

                        {/* Qty Input */}
                        <TableCell align="center" sx={cellSx}>
                          <TextField
                            type="number"
                            size="small"
                            value={item.quantity}
                            onChange={(e) =>
                              handleBillItemChange(
                                idx,
                                "quantity",
                                e.target.value,
                              )
                            }
                            onKeyDown={handleNumberKeyDown}
                            inputProps={{
                              style: {
                                width: 40,
                                textAlign: "center",
                                fontSize: "0.8rem",
                              },
                              min: 0,
                            }}
                            disabled={isProcessing}
                            sx={{
                              ...commonInputStyles,
                              "& .MuiOutlinedInput-root": {
                                padding: "2px 4px",
                              },
                              "& input": {
                                padding: "4px 0",
                                fontSize: "0.8rem",
                              },
                            }}
                          />
                        </TableCell>

                        {/* Base Rate */}
                        <TableCell align="right" sx={{ ...cellSx, pr: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.75rem" }}
                          >
                            ₹{item.base_rate.toFixed(2)}
                          </Typography>
                        </TableCell>

                        {/* Type Select */}
                        <TableCell align="center" sx={cellSx}>
                          <Select
                            size="small"
                            value={item.type}
                            onChange={(e) =>
                              handleBillItemChange(idx, "type", e.target.value)
                            }
                            disabled={isProcessing}
                            sx={{
                              fontSize: "0.7rem",
                              "& .MuiSelect-select": {
                                padding: "4px 8px",
                                fontSize: "0.7rem",
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#ccc",
                              },
                            }}
                          >
                            <MenuItem
                              value="non-scheme"
                              sx={{ fontSize: "0.7rem" }}
                            >
                              Non
                            </MenuItem>
                            {eligible.length > 0 && (
                              <MenuItem
                                value="scheme"
                                sx={{ fontSize: "0.7rem" }}
                              >
                                Scheme
                              </MenuItem>
                            )}
                          </Select>
                        </TableCell>

                        {/* Scheme Autocomplete */}
                        <TableCell align="center" sx={{ ...cellSx, px: 0.3 }}>
                          <Autocomplete
                            size="small"
                            options={eligible}
                            getOptionLabel={(o) => o.name}
                            value={selected || null}
                            onChange={(e, v) =>
                              handleBillItemChange(idx, "scheme_id", v)
                            }
                            disabled={item.type !== "scheme" || isProcessing}
                            renderOption={renderSchemeOption}
                            sx={{
                              ...commonInputStyles,
                              "& .MuiOutlinedInput-root": { padding: "0 4px" },
                              "& input": {
                                fontSize: "0.65rem",
                                padding: "4px 2px",
                              },
                            }}
                            renderInput={(p) => (
                              <TextField
                                {...p}
                                placeholder="Select"
                                sx={{
                                  "& input::placeholder": {
                                    fontSize: "0.6rem",
                                  },
                                }}
                              />
                            )}
                          />
                        </TableCell>

                        {/* Percentage */}
                        <TableCell align="center" sx={cellSx}>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{
                              fontSize: "0.75rem",
                              color:
                                item.type === "scheme" ? "#d84315" : "grey",
                            }}
                          >
                            {item.type === "scheme" && selected
                              ? `${selected.percentage}%`
                              : "-"}
                          </Typography>
                        </TableCell>

                        {/* Final Rate */}
                        <TableCell align="right" sx={{ ...cellSx, pr: 1 }}>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{
                              fontSize: "0.75rem",
                              color:
                                item.type === "scheme"
                                  ? "secondary.main"
                                  : "text.primary",
                            }}
                          >
                            ₹{item.final_rate.toFixed(2)}
                          </Typography>
                        </TableCell>

                        {/* Line Total */}
                        <TableCell align="right" sx={{ ...cellSx, pr: 1 }}>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ fontSize: "0.75rem" }}
                          >
                            ₹{item.line_total.toFixed(2)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {/* ✅ TOTAL AMOUNT ROW */}
                  <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                    <TableCell
                      colSpan={7}
                      align="right"
                      sx={{
                        fontWeight: 700,
                        py: 1.5,
                        pr: 1,
                        fontSize: "0.8rem",
                      }}
                    >
                      Total Amount:
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ pr: 1, fontWeight: 700, fontSize: "0.85rem" }}
                    >
                      ₹{billTotals.subtotal.toFixed(2)}
                    </TableCell>
                  </TableRow>

                  {/* ✅ DISCOUNT ROW */}
                  {billTotals.discountTotal > 0 && (
                    <TableRow sx={{ bgcolor: "#fff3e0" }}>
                      <TableCell
                        colSpan={7}
                        align="right"
                        sx={{
                          color: "error.main",
                          fontWeight: 600,
                          py: 1,
                          pr: 1,
                          fontSize: "0.8rem",
                        }}
                      >
                        Discount:
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          pr: 1,
                          color: "error.main",
                          fontWeight: 700,
                          fontSize: "0.85rem",
                        }}
                      >
                        - ₹{billTotals.discountTotal.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  )}

                  {/* ✅ GRAND TOTAL ROW */}
                  <TableRow sx={{ bgcolor: "#e8f5e9" }}>
                    <TableCell
                      colSpan={7}
                      align="right"
                      sx={{
                        fontWeight: 800,
                        py: 2,
                        pr: 1,
                        fontSize: "0.95rem",
                        color: "#1b5e20",
                      }}
                    >
                      Grand Total:
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        pr: 1,
                        fontWeight: 800,
                        fontSize: "1rem",
                        color: "#2e7d32",
                      }}
                    >
                      ₹{billTotals.grandTotal.toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 1.5 }} />

            <FormControl fullWidth size="small" disabled={isProcessing}>
              <InputLabel sx={{ fontSize: "0.85rem" }}>
                Payment Method
              </InputLabel>
              <Select
                value={billPaymentType}
                label="Payment Method"
                onChange={(e) => setBillPaymentType(e.target.value)}
                sx={{ fontSize: "0.85rem" }}
              >
                <MenuItem value="Cash" sx={{ fontSize: "0.85rem" }}>
                  💵 Cash
                </MenuItem>
                <MenuItem value="UPI" sx={{ fontSize: "0.85rem" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    📱 UPI
                    <Chip
                      label="Online"
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: 18, fontSize: ".6rem" }}
                    />
                  </Box>
                </MenuItem>
                {/* <MenuItem value="Free of Cost" sx={{ fontSize: "0.85rem" }}>🆓 Free of Cost</MenuItem> */}
              </Select>
            </FormControl>

            {billPaymentType === "UPI" && !isProcessing && (
              <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                  <strong>UPI Payment:</strong> Click "Confirm & Pay" to open
                  Razorpay. Complete payment to generate the bill.
                </Typography>
              </Alert>
            )}
          </DialogContent>

          <DialogActions
            sx={{ p: 2, bgcolor: "#fafafa", flexDirection: "column", gap: 1 }}
          >
            {billPaymentType === "UPI" && (
              <Box
                sx={{
                  width: "80%",
                  bgcolor: "#e8f5e9",
                  p: 1.5,
                  borderRadius: 2,
                  textAlign: "center",
                  border: "2px solid #2e7d32",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem" }}
                >
                  Amount to Pay
                </Typography>
                <Typography variant="h5" fontWeight={800} color="#2e7d32">
                  ₹{billTotals.grandTotal.toFixed(2)}
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                width: "100%",
                justifyContent: "flex-end",
              }}
            >
              <Button
                onClick={() => setBillModalOpen(false)}
                color="inherit"
                sx={{ borderRadius: 2 }}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleBillSubmit}
                disabled={isProcessing || billItems.length === 0}
                sx={{ borderRadius: 2, px: 4, boxShadow: 2 }}
                startIcon={billPaymentType === "UPI" ? <Payment /> : null}
              >
                {isProcessing ? (
                  <CircularProgress size={24} color="inherit" />
                ) : billPaymentType === "UPI" ? (
                  `Confirm & Pay ₹${billTotals.grandTotal.toFixed(2)}`
                ) : (
                  "Confirm & Bill"
                )}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* ═════════════════ PDF PREVIEW ═════════════════ */}
        <Dialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              ...paperSx,
              height: { xs: "95vh", md: "90vh" },
              maxHeight: "95vh",
              display: "flex",
              flexDirection: "column",
              bgcolor: "#000",
            },
          }}
        >
          <DialogTitle
            sx={{
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
              bgcolor: "#1a1a1a",
              borderBottom: "1px solid #333",
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ color: "#ff9800" }}>
              Invoice Preview
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton
                onClick={() =>
                  selectedOrder && handleDownloadBill(selectedOrder.orderid)
                }
                sx={{ color: "#2e7d32" }}
              >
                <Download />
              </IconButton>
              <IconButton
                onClick={() => setPreviewOpen(false)}
                sx={{ color: "#ff9800" }}
              >
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent
            sx={{
              p: 0,
              flexGrow: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              bgcolor: "#000",
            }}
          >
            {pdfLoading ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <CircularProgress size={50} sx={{ color: "#ff9800" }} />
                <Typography sx={{ color: "#2e7d32", fontWeight: 600 }}>
                  Generating PDF...
                </Typography>
              </Box>
            ) : pdfUrl ? (
              <iframe
                src={pdfUrl}
                width="100%"
                height="100%"
                style={{ border: "none" }}
                title="PDF Preview"
              />
            ) : (
              <Typography sx={{ color: "#ff9800" }}>
                Failed to load PDF.
              </Typography>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </>
  );
};

export default SpeciesRequest;
